// Task completion status detection utilities

/**
 * List of status terms that indicate task completion
 * This includes various ways users might express task completion
 */
export const COMPLETION_STATUSES = [
  // Primary completion terms
  'Done',
  'Complete', 
  'Completed',
  'Finished',
  'Closed',
  
  // Alternative completion terms
  'Resolved',
  'Solved',
  'Fixed',
  'Delivered',
  'Shipped',
  'Released',
  'Approved',
  'Accepted',
  'Verified',
  'Validated',
  'Final',
  'End',
  'Success',
  'Accomplished',
  'Achieved',
  'Concluded'
];

/**
 * Check if a status indicates task completion
 * @param {string} status - The task status to check
 * @returns {boolean} - True if status indicates completion
 */
export const isCompletionStatus = (status) => {
  if (!status) return false;
  
  // Handle status being passed as an object (extract name)
  const statusString = typeof status === 'object' ? (status.name || status.label || '') : status;
  
  if (!statusString || typeof statusString !== 'string') return false;
  
  const normalizedStatus = statusString.toLowerCase().trim();
  
  return COMPLETION_STATUSES.some(completionStatus => 
    normalizedStatus === completionStatus.toLowerCase() ||
    normalizedStatus.includes(completionStatus.toLowerCase())
  );
};

/**
 * Check if a status change represents moving to completion
 * @param {string} oldStatus - Previous status
 * @param {string} newStatus - New status
 * @returns {boolean} - True if changing from non-complete to complete
 */
export const isMovingToCompletion = (oldStatus, newStatus) => {
  const wasComplete = isCompletionStatus(oldStatus);
  const willBeComplete = isCompletionStatus(newStatus);
  
  return !wasComplete && willBeComplete;
};

/**
 * Get incomplete subtasks from a task
 * @param {Object} task - Task object with subtasks
 * @returns {Array} - Array of incomplete subtasks
 */
export const getIncompleteSubtasks = (task) => {
  if (!task || !task.subtasks || !Array.isArray(task.subtasks)) {
    return [];
  }
  
  return task.subtasks.filter(subtask => !subtask.completed);
};

/**
 * Check if a task has incomplete subtasks
 * @param {Object} task - Task object with subtasks
 * @returns {boolean} - True if task has incomplete subtasks
 */
export const hasIncompleteSubtasks = (task) => {
  const incompleteSubtasks = getIncompleteSubtasks(task);
  return incompleteSubtasks.length > 0;
};

/**
 * Validate if task completion should be allowed
 * @param {Object} task - Task object
 * @param {string} newStatus - New status being set
 * @returns {Object} - Validation result { canComplete, incompleteSubtasks, reason }
 */
export const validateTaskCompletion = (task, newStatus) => {
  // Check if moving to completion
  if (!isMovingToCompletion(task.status, newStatus)) {
    return {
      canComplete: true,
      incompleteSubtasks: [],
      reason: 'Not moving to completion status'
    };
  }
  
  // Check for incomplete subtasks
  const incompleteSubtasks = getIncompleteSubtasks(task);
  
  if (incompleteSubtasks.length === 0) {
    return {
      canComplete: true,
      incompleteSubtasks: [],
      reason: 'No incomplete subtasks'
    };
  }
  
  return {
    canComplete: false,
    incompleteSubtasks,
    reason: `Task has ${incompleteSubtasks.length} incomplete subtask${incompleteSubtasks.length !== 1 ? 's' : ''}`
  };
};